import type { ReactNode } from 'react';
import { motion } from 'framer-motion';
import { clsx } from 'clsx';
import { hoverLift, shouldReduceMotion } from '@/lib/animations/optimized-animations';
import { GlassPanel } from './GlassPanel';

export type GlowColor = 'cyan' | 'green' | 'orange' | 'violet';
export type GlowIntensity = 'low' | 'med' | 'high';

export interface GlowCardProps {
  children?: ReactNode;
  /** Accent role for the glow border. Defaults to 'cyan' (proof/citations). */
  glow?: GlowColor;
  /** Scales the glow shadow strength. Defaults to 'med'. */
  intensity?: GlowIntensity;
  className?: string;
}

// Base shadow per accent. cyan/green/orange use the provided glow vars;
// violet uses a tinted shadow referencing the --violet token color.
const GLOW_SHADOW: Record<GlowColor, string> = {
  cyan: 'var(--glow-cyan)',
  green: 'var(--glow-green)',
  orange: 'var(--glow-orange)',
  violet: '0 0 22px rgba(167, 139, 250, 0.35)',
};

// Intensity scales the spread/opacity by layering the base shadow.
const INTENSITY_SCALE: Record<GlowIntensity, number> = {
  low: 1,
  med: 2,
  high: 3,
};

function buildBoxShadow(glow: GlowColor, intensity: GlowIntensity): string {
  const base = GLOW_SHADOW[glow];
  const layers = INTENSITY_SCALE[intensity];
  return Array.from({ length: layers }, () => base).join(', ');
}

/**
 * Glass card with a token-driven glow border. Lifts and intensifies on hover
 * unless reduced motion is active, in which case it renders a static glow.
 */
export function GlowCard({
  children,
  glow = 'cyan',
  intensity = 'med',
  className,
}: GlowCardProps) {
  const reduce = shouldReduceMotion();
  const boxShadow = buildBoxShadow(glow, intensity);

  if (reduce) {
    return (
      <div
        className={clsx('rounded-2xl', className)}
        style={{ boxShadow }}
        data-glow={glow}
        data-intensity={intensity}
      >
        <GlassPanel>{children}</GlassPanel>
      </div>
    );
  }

  return (
    <motion.div
      className={clsx('rounded-2xl', className)}
      style={{ boxShadow, willChange: 'transform' }}
      data-glow={glow}
      data-intensity={intensity}
      whileHover={hoverLift.whileHover}
      whileTap={hoverLift.whileTap}
    >
      <GlassPanel>{children}</GlassPanel>
    </motion.div>
  );
}

export default GlowCard;
